import "./MissionBillsTable.scss";
import InfoContext from "pages/InfoContext";
import InputText from "core/inputs/input-text/InputText";
import InputTextArea from "core/inputs/input-text-area/InputTextArea";
import PropTypes from "prop-types";
import _ from "lodash";
import { VAR_TYPES } from "constants/constants";
import { getCurrentYearMonths } from "utils/missionHelper";
import { useContext } from "react";
import { useIntl } from "react-intl";
import { onClickCell } from "utils/functions";
import TableDateNav from "components/table-date-nav/TableDateNav";

const MissionBillsTable = ({ setValue }) => {
  const intl = useIntl();
  const {
    tableBills,
    missionById,
    selectedMissionBillsTableDate,
    setSelectedMissionBillsTableDate,
    selectedMissionBillsTableYear,
    setSelectedMissionBillsTableYear,
  } = useContext(InfoContext);

  const missionStartDate =
    missionById?.start_date && new Date(missionById.start_date);
  const missionEndDate =
    missionById?.end_date && new Date(missionById.end_date);

  const tableLines = [
    intl.formatMessage({
      id: "turnover_billed",
      defaultMessage: "CA facturé",
    }),
    intl.formatMessage({
      id: "bill_number",
      defaultMessage: "N° de facture",
    }),
    intl.formatMessage({
      id: "to_bill",
      defaultMessage: "FAE",
    }),
    intl.formatMessage({
      id: "to_bill_comment",
      defaultMessage: "FAE commentaire",
    }),
  ];

  const months = _.concat(
    [
      <TableDateNav
        key={"year-navigation"}
        selectedDate={selectedMissionBillsTableDate}
        setSelectedDate={setSelectedMissionBillsTableDate}
        setSelectedYear={setSelectedMissionBillsTableYear}
      />,
    ],
    getCurrentYearMonths({ selectedYear: selectedMissionBillsTableYear })
  );

  const onChangeInput = ({ month, year, field, value }) => {
    const newData = () => {
      if (
        tableBills?.some(
          (elm) => elm.bill_month === month && elm.bill_year === year
        )
      ) {
        return tableBills?.map((elm) =>
          elm.bill_month === month && elm.bill_year === year
            ? { ...elm, [field]: value, isUpdatedBill: true }
            : elm
        );
      } else {
        return [
          ...tableBills,
          {
            isNewBill: true,
            bill_month: month,
            bill_year: year,
            mission_id: missionById?.id,
            [field]: value,
          },
        ];
      }
    };
    setValue("table_bills", newData());
  };

  const getFirstColumn = (line) => {
    switch (line) {
      case 0:
        return <td className="line-name">{tableLines[0]}</td>;
      case 1:
        return <td className="line-name">{tableLines[1]}</td>;
      case 2:
        return <td className="line-name">{tableLines[2]}</td>;
      case 3:
        return <td className="line-name">{tableLines[3]}</td>;
      default:
        return;
    }
  };

  const getColumnInput = ({ line, column }) => {
    const columnDate = new Date(
      `${selectedMissionBillsTableYear + "-" + column + "-01"}`
    );

    const columnDateYear = columnDate?.getFullYear();
    const columnDateMonth = columnDate?.getMonth();
    const missionStartDateYear = missionStartDate?.getFullYear();
    const missionStartDateMonth = missionStartDate?.getMonth();
    const missionEndDateYear = missionEndDate?.getFullYear();
    const missionEndDateMonth = missionEndDate?.getMonth();

    const isEditable =
      (columnDateYear > missionStartDateYear ||
        (columnDateYear === missionStartDateYear &&
          columnDateMonth >= missionStartDateMonth)) &&
      (columnDateYear < missionEndDateYear ||
        (columnDateYear === missionEndDateYear &&
          columnDateMonth <= missionEndDateMonth) ||
        !missionEndDate);

    const fieldMapping = [
      {
        type: VAR_TYPES.NUMBER,
        field: "turnover_billed",
        component: InputText,
      },
      { type: VAR_TYPES.TEXT, field: "bill_number", component: InputText },
      { type: VAR_TYPES.NUMBER, field: "to_bill", component: InputText },
      { type: null, field: "to_bill_comment", component: InputTextArea },
    ];

    const mapping = fieldMapping[line];
    if (!mapping) return null;

    const { type, field, component: Component } = mapping;

    const value = _.chain(tableBills)
      .find({ bill_month: column, bill_year: selectedMissionBillsTableYear })
      .get(field)
      .value();

    return (
      <td
        key={`line-${line}_column-${column}`}
        className={!isEditable ? "cell-disabled" : "cell-active"}
        onClick={() =>
          onClickCell(`input-${line === 3 ? "area" : "text"}-${line}-${column}`)
        }
      >
        <Component
          type={type}
          value={value || undefined}
          onChange={(e) => {
            onChangeInput({
              month: column,
              year: selectedMissionBillsTableYear,
              field,
              value: e.target.value,
            });
          }}
          readOnly={!isEditable}
          id={`${line}-${column}`}
          width={"100%"}
        />
      </td>
    );
  };

  return (
    <div className="table-overflow">
      <table className="editable-cell-table">
        <thead>
          <tr>
            {months.map((column, index) => (
              <th className="column-name" key={index}>
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableLines.map((row, index) => (
            <tr key={row}>
              {_.times(13, (columnIndex) =>
                columnIndex === 0
                  ? getFirstColumn(index)
                  : getColumnInput({
                      line: index,
                      column: columnIndex,
                    })
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

MissionBillsTable.propTypes = {
  setValue: PropTypes.func,
};

export default MissionBillsTable;
